import WRAP from "WRAP-JS";
import * as ConfigRadar from "./config/ConfigRadar";
import { formatTimeZoneDate, translation } from "../../../utils/FPEUtils";
import LegendRadar from "../legend/LegendRadar";
import { WX_ID_RADAR } from "../../../store/WeatherContentsState";
import TimeController from "../helper/TimeController";
import { nearValidTime } from "../helper/MapUtil";
import MissingDataObserver from "../helper/MissingDataObserver";
import { COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER, COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD } from "../../../constants/ErrorMessage";

export const RADAR_TYPE_JP = 'Japan';
export const RADAR_TYPE_KR = 'Korea';
export const RADAR_TYPE_TW = 'Taiwan';
export const RADAR_TYPE_XRAIN = 'XRAIN';
export const RADAR_TYPE_NOWCAST = 'NOWCAST';
export const RADAR_TYPE_ECHO_TOP_JP = 'JapanEchoTop';
export const RADAR_TYPE_JMA_INFO = 'JmaInfo';
export const RADAR_TYPE_JMA_PRCINT = 'PRCINT';
export const RADAR_TYPE_PRCINT_ECHO_TOP = 'prcintEchoTop';
export const RADAR_TYPE_JMA_PRCINT_NAME = 'Japan High Resolution';

const MISSING_THRESHOLD = {
  [RADAR_TYPE_JP]: 1800,
  [RADAR_TYPE_KR]: 1800,
  [RADAR_TYPE_TW]: 1800,
  [RADAR_TYPE_XRAIN]: 1800,
  [RADAR_TYPE_NOWCAST]: 0,
  [RADAR_TYPE_ECHO_TOP_JP]: 1800,
  [RADAR_TYPE_JMA_PRCINT]: 1800,
  [RADAR_TYPE_PRCINT_ECHO_TOP]: 1800
};

class Radar {
  static NAME = WX_ID_RADAR;

  // 日本
  dhJP;
  layerJP;
  layerCoverageJP;
  // 韓国
  dhKR;
  layerKR;
  layerCoverageKR;
  // 台湾
  dhTW;
  layerTW;
  layerCoverageTW;
  // XRAIN
  dhXRAIN;
  layerXRAIN;
  layerCoverageXRAIN;
  // NOWCAST
  dhNOWCAST;
  layerNOWCAST;
  layerCoverageNOWCAST;
  //日本 EchoTop
  dhEchoTopJP;
  layerEchoTopJP;
  //JMA運用情報
  dhJmaInfoDh;
  //レーダータイムコントローラー
  radarTimeController;
  //タイムコントローラーのリスト
  timeList;
  //自動更新フラグ
  autoRefresh = true;
  // 気象庁全国合成レーダー
  dhPrcint;
  layerPrcint;
  layerCoveragePrcint;
  //気象庁全国合成 EchoTop
  dhPrcintEchoTop;
  layerPrcintEchoTop;

  dhAutoUpdate = {
    [RADAR_TYPE_JP]: true,
    [RADAR_TYPE_KR]: true,
    [RADAR_TYPE_TW]: true,
    [RADAR_TYPE_XRAIN]: true,
    [RADAR_TYPE_NOWCAST]: true,
    [RADAR_TYPE_ECHO_TOP_JP]: true,
    [RADAR_TYPE_JMA_INFO]: true,
    [RADAR_TYPE_JMA_PRCINT]: true,
    [RADAR_TYPE_PRCINT_ECHO_TOP]: true
  };

  areaInitialized = {
    [RADAR_TYPE_JP]: false,
    [RADAR_TYPE_KR]: false,
    [RADAR_TYPE_TW]: false,
    [RADAR_TYPE_XRAIN]: false,
    [RADAR_TYPE_NOWCAST]: false,
    [RADAR_TYPE_ECHO_TOP_JP]: false,
    [RADAR_TYPE_JMA_INFO]: false,
    [RADAR_TYPE_JMA_PRCINT]: false,
    [RADAR_TYPE_PRCINT_ECHO_TOP]: false
  };

  areaValidtimeInfo = {
    [RADAR_TYPE_JP]: '---',
    [RADAR_TYPE_KR]: '---',
    [RADAR_TYPE_TW]: '---',
    [RADAR_TYPE_XRAIN]: '---',
    [RADAR_TYPE_NOWCAST]: '---',
    [RADAR_TYPE_ECHO_TOP_JP]: '---',
    [RADAR_TYPE_JMA_INFO]: false,
    [RADAR_TYPE_JMA_PRCINT]: '---',
    [RADAR_TYPE_PRCINT_ECHO_TOP]: '---'
  };

  areaVisible = {
    [RADAR_TYPE_JP]: false,
    [RADAR_TYPE_KR]: false,
    [RADAR_TYPE_TW]: false,
    [RADAR_TYPE_XRAIN]: false,
    [RADAR_TYPE_NOWCAST]: false,
    [RADAR_TYPE_ECHO_TOP_JP]: false,
    [RADAR_TYPE_JMA_INFO]: false,
    [RADAR_TYPE_JMA_PRCINT]: false,
    [RADAR_TYPE_PRCINT_ECHO_TOP]: false
  }

  areaMissing = {
    [RADAR_TYPE_JP]: false,
    [RADAR_TYPE_KR]: false,
    [RADAR_TYPE_TW]: false,
    [RADAR_TYPE_XRAIN]: false,
    [RADAR_TYPE_NOWCAST]: false,
    [RADAR_TYPE_ECHO_TOP_JP]: false,
    [RADAR_TYPE_JMA_INFO]: false,
    [RADAR_TYPE_JMA_PRCINT]: false,
    [RADAR_TYPE_PRCINT_ECHO_TOP]: false
  };

  coverage;
  echoTop;
  prcintEchoTop;
  transmittance = 47;

  ready = false;
  etd;
  elapsed = 0;
  dispatcher;
  visible;
  currentMergeTargetLayer;
  errorMessageInfo = {};

  constructor(config) {
    this.echoTop = !!config.echoTop;
    this.prcintEchoTop = !!config.prcintEchoTop;
    this.coverage = true;

    this.layerJP = new WRAP.Geo.Layer("RadarJP_ICDB");
    this.layerKR = new WRAP.Geo.Layer("RadarKR");
    this.layerTW = new WRAP.Geo.Layer("RadarTW");
    this.layerXRAIN = new WRAP.Geo.Layer("RadarXRAIN");
    this.layerNOWCAST = new WRAP.Geo.Layer("RadarNOWCAST");
    this.layerPrcint = new WRAP.Geo.Layer("RadarJMAPrcint");
    this.layerEchoTopJP = new WRAP.Geo.Layer("RadarEhoTopJP_ICDB");
    this.layerPrcintEchoTop = new WRAP.Geo.Layer("RadarPrcintEchoTop");
    this.layerCoverageJP = new WRAP.Geo.Layer("COVERAGE_JP");
    this.layerCoverageKR = new WRAP.Geo.Layer("COVERAGE_KR");
    this.layerCoverageTW = new WRAP.Geo.Layer("COVERAGE_TW");
    this.layerCoverageXRAIN = new WRAP.Geo.Layer("COVERAGE_XRAIN");
    this.layerCoverageNOWCAST = new WRAP.Geo.Layer("COVERAGE_NOWCAST");
    this.layerCoveragePrcint = new WRAP.Geo.Layer("COVERAGE_PRCINT");
    this.layerCoverageJP.toString = () => {
      return RADAR_TYPE_JP;
    };
    this.layerCoverageKR.toString = () => {
      return RADAR_TYPE_KR;
    };
    this.layerCoverageTW.toString = () => {
      return RADAR_TYPE_TW;
    };
    this.layerCoverageXRAIN.toString = () => {
      return RADAR_TYPE_XRAIN;
    };
    this.layerCoverageNOWCAST.toString = () => {
      return RADAR_TYPE_NOWCAST;
    };
    this.layerCoveragePrcint.toString = () => {
      return RADAR_TYPE_JMA_PRCINT;
    }

    this.layerJP.setVisible(false);
    this.layerKR.setVisible(false);
    this.layerTW.setVisible(false);
    this.layerXRAIN.setVisible(false);
    this.layerNOWCAST.setVisible(false);
    this.layerPrcint.setVisible(false);
    this.layerEchoTopJP.setVisible(false);
    this.layerPrcintEchoTop.setVisible(false);
    this.layerCoverageJP.setVisible(false);
    this.layerCoverageKR.setVisible(false);
    this.layerCoverageTW.setVisible(false);
    this.layerCoverageXRAIN.setVisible(false);
    this.layerCoverageNOWCAST.setVisible(false);
    this.layerCoveragePrcint.setVisible(false);

    this.layerJP.disableInteraction(true);
    this.layerKR.disableInteraction(true);
    this.layerTW.disableInteraction(true);
    this.layerXRAIN.disableInteraction(true);
    this.layerNOWCAST.disableInteraction(true);
    this.layerPrcint.disableInteraction(true);
    this.layerEchoTopJP.disableInteraction(true);
    this.layerPrcintEchoTop.disableInteraction(true);
    this.layerCoverageJP.disableInteraction(true);
    this.layerCoverageKR.disableInteraction(true);
    this.layerCoverageTW.disableInteraction(true);
    this.layerCoverageXRAIN.disableInteraction(true);
    this.layerCoverageNOWCAST.disableInteraction(true);
    this.layerCoveragePrcint.disableInteraction(true);

    this.radarTimeController = new TimeController(WX_ID_RADAR, {
      label: 'Radar',
      getTimeList: () => {
        if (this.timeList) {
          return this.timeList;
        }
        this._updateTimeList();
        return this.timeList;
      },
      updated: () => {
        this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
      },
      changeAutoRefresh: (autoFlg) => {
        this.setAutoRefresh(autoFlg);
      }
    });
  }

  setDispatcher(dispatcher) {
    this.dispatcher = dispatcher;
    this.missingDataObserver = new MissingDataObserver(this.dispatcher, MISSING_THRESHOLD, this.areaVisible);
  }

  setJmaInfoDispatcher(dispatcher) {
    this.jmaDispatcher = dispatcher;
  }

  async initialize(dataType) {
    await this.initData(dataType);
    this.initLayer(dataType);
    this.initTimeController(dataType);
    this.ready = true;
  }

  initData(dataType) {
    return new Promise(resolve => {
      let dh;
      let promises;

      switch (dataType) {
        case RADAR_TYPE_JP:
          dh = this.dhJP = new WRAP.Data(ConfigRadar.configRadarDhJP);
          this.dhJP.validate((displayTime, data, content) => {
            return this._validateTime(RADAR_TYPE_JP, data, displayTime, content);
          });
          this.missingDataObserver.monitoring(RADAR_TYPE_JP, this.dhJP, this.layerJP, {callback: this._updateMissing.bind(this)});
          break;
        case RADAR_TYPE_KR:
          dh = this.dhKR = new WRAP.Data(ConfigRadar.configRadarDhKR);
          this.dhKR.validate((displayTime, data, content) => {
            return this._validateTime(RADAR_TYPE_KR, data, displayTime, content);
          });
          this.missingDataObserver.monitoring(RADAR_TYPE_KR, this.dhKR, this.layerKR, {callback: this._updateMissing.bind(this)});
          break;
        case RADAR_TYPE_TW:
          dh = this.dhTW = new WRAP.Data(ConfigRadar.configRadarDhTW);
          this.dhTW.validate((displayTime, data, content) => {
            return this._validateTime(RADAR_TYPE_TW, data, displayTime, content);
          });
          this.missingDataObserver.monitoring(RADAR_TYPE_TW, this.dhTW, this.layerTW, {callback: this._updateMissing.bind(this)});
          break;
        case RADAR_TYPE_XRAIN:
          dh = this.dhXRAIN = new WRAP.Data(ConfigRadar.configRadarDhXRAIN);
          this.dhXRAIN.validate((displayTime, data, content) => {
            return this._validateTime(RADAR_TYPE_XRAIN, data, displayTime, content);
          });
          this.missingDataObserver.monitoring(RADAR_TYPE_XRAIN, this.dhXRAIN, this.layerXRAIN, {callback: this._updateMissing.bind(this)});
          break;
        case RADAR_TYPE_NOWCAST:
          dh = this.dhNOWCAST = new WRAP.Data(ConfigRadar.configRadarDhNowCast);
          this.dhNOWCAST.validate((displayTime, data, content) => {
            return this._validateTime(RADAR_TYPE_NOWCAST, data, displayTime, content);
          });
          this.missingDataObserver.monitoring(RADAR_TYPE_NOWCAST, this.dhNOWCAST, this.layerNOWCAST, {callback: this._updateMissing.bind(this)});
          break;
        case RADAR_TYPE_ECHO_TOP_JP:
          dh = this.dhEchoTopJP = new WRAP.Data(ConfigRadar.configRadarDhEchoTopJP);
          this.dhEchoTopJP.validate((displayTime, data, content) => {
            const vt = this._nearValidTime(data.retained.validtime, displayTime);
            //displayTimeがJPレーダーと一致しない場合はエコートップは表示しない
            if (vt && this.areaValidtimeInfo[RADAR_TYPE_JP] === vt) {
              content.validtime = vt;
              return true;
            }
            return false
          });
          this.missingDataObserver.monitoring(RADAR_TYPE_ECHO_TOP_JP, this.dhEchoTopJP, this.layerEchoTopJP, {callback: this._updateMissing.bind(this)});
          break;
        case RADAR_TYPE_JMA_INFO:
          dh = this.dhJmaInfoDh = new WRAP.Data(ConfigRadar.configRadarJmaInfoDh);
          break;
        case RADAR_TYPE_JMA_PRCINT:
          dh = this.dhPrcint = new WRAP.Data(ConfigRadar.configRadarPrcintDh);
          this.dhPrcint.validate((displayTime, data, content) => {
            return this._validateTime(RADAR_TYPE_JMA_PRCINT, data, displayTime, content);
          });
          this.missingDataObserver.monitoring(RADAR_TYPE_JMA_PRCINT, this.dhPrcint, this.layerPrcint, {callback: this._updateMissing.bind(this)});
          break;
        case RADAR_TYPE_PRCINT_ECHO_TOP:
          dh = this.dhPrcintEchoTop = new WRAP.Data(ConfigRadar.configRadarPrcintEchoTopDh);
          this.dhPrcintEchoTop.validate((displayTime, data, content) => {
            const vt = this._nearValidTime(data.retained.validtime, displayTime);
            content.validtime = vt;
            return true;
          });
          this.missingDataObserver.monitoring(RADAR_TYPE_PRCINT_ECHO_TOP, this.dhPrcintEchoTop, this.layerPrcintEchoTop, {callback: this._updateMissing.bind(this)});
          break;
        default:
      }

      if (dh && dh.conf.validtime) {
        dh.updated('validtime', ref => {
          this._selectValidTime(ref, dataType);
          this.areaInitialized[dataType] = true;
          resolve(ref);
        });
        if (dh.conf.timelist) {
          dh.updated('timelist', () => {
            if (this.errorMessageInfo[`${dataType}_timelist`]) {
              this.dispatcher({type: 'CLEAR_ERROR', payload: this.errorMessageInfo[`${dataType}_timelist`]});
              this.errorMessageInfo[`${dataType}_timelist`] = null;
            }
          }, () => {
            this.errorMessageInfo[`${dataType}_timelist`] = this.dispatcher({type: 'SHOW_ERROR', payload: {
              message: `${dh.conf.data.Name} timelist`},
              additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
            });
          });
        }
        if (dh.conf.region) {
          dh.updated('region', null, () => {
            this.dispatcher({type: 'SHOW_ERROR', payload: {
              message: `${dh.conf.data.Name} region`,
              additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD
            }});
          });
        }
      } else if (dh && dh.conf.timelist) {
        dh.updated('timelist', ref => {
          this._selectValidTime(ref, dataType);
          this.areaInitialized[dataType] = true;
          resolve(ref);
        });
      } else {
        dh.updated('data', ref => {
          if (this.errorMessageInfo[`${dataType}_data`]) {
            this.dispatcher({type: 'CLEAR_ERROR', payload: this.errorMessageInfo[`${dataType}_data`]});
            this.errorMessageInfo[`${dataType}_data`] = null;
          }
          this.areaInitialized[dataType] = true;
          resolve(ref);
          this.jmaDispatcher(ref.data);
        }, () => {
          this.errorMessageInfo[`${dataType}_data`] = this.dispatcher({type: 'SHOW_ERROR', payload: {
            message: `${dh.conf.data.Name} data`,
            additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
          }});
          this.jmaDispatcher({error: true});
        });

      }

      if (promises && promises.length) {
        Promise.all(promises).then(refs => {
          resolve(refs);
        });
      }
    });
  }

  initLayer(dataType) {
    const target = this._getTarget(dataType);
    if (Array.isArray(target.layer)){
      for (let i = 0; i < target.layer.length; i++) {
        target.layer[i].configure(target.dh, target.layerConfig[i]);
      }
    } else if (target.layer) {
      target.layer.configure(target.dh, target.layerConfig);
    }
  }

  initTimeController(dataType) {
    const target = this._getTarget(dataType);
    if (!target.timeCtrlLayer) return;
    [].concat(target.timeCtrlLayer).forEach(layer=>{
      this.radarTimeController.addLayer(layer);
    });

    if (dataType === RADAR_TYPE_JP) {
      this.radarTimeController.setDisplayTime(this.areaValidtimeInfo[RADAR_TYPE_JP]);
    }
  }

  async drawLayer(visible, type) {
    if (!type) {
      return;
    }

    this.areaVisible[type] = visible;
    if (type === RADAR_TYPE_JP) {
      this.areaVisible[RADAR_TYPE_JMA_INFO] = visible;
      type = [type, RADAR_TYPE_JMA_INFO];
      if (!visible || (visible && this.echoTop)) {
        this.areaVisible[RADAR_TYPE_ECHO_TOP_JP] = visible && this.echoTop;
        type.push(RADAR_TYPE_ECHO_TOP_JP);
      }
    } else if (type === RADAR_TYPE_JMA_PRCINT) {
      this.areaVisible[RADAR_TYPE_JMA_INFO] = visible;
      type = [type, RADAR_TYPE_JMA_INFO];
      if (!visible || (visible && this.prcintEchoTop)) {
        this.areaVisible[RADAR_TYPE_PRCINT_ECHO_TOP] = visible && this.prcintEchoTop;
        type.push(RADAR_TYPE_PRCINT_ECHO_TOP);
      }
    }
    this.visible = Object.values(this.areaVisible).some(v => v);

    for (const t of [].concat(type)) {
      if (!this.areaInitialized[t] && visible) {
        await this.initialize(t);
      }
      this.setVisible(visible, t);
    }
  }

  getLayer() {
    return this._allLayers();
  }

  setVisible(visible, type) {
    if (!this.areaInitialized[type]) {
      return;
    }

    const target = this._getTarget(type);

    if (Array.isArray(target.layer)) {
      if (target.layer[0].visible() === visible) {
        return;
      }

      for (let i = 0; i < target.layer.length; i++) {
        if (visible) {
          if (!this.coverage && target.layer[i]._conf.Attributes.layer_type === 'coverage') {
            continue;
          }
          target.layer[i].show();
        } else {
          target.layer[i].hide();
        }
      }

    } else if (target.layer) {
      if (target.layer.visible() === visible) {
        return;
      }

      if (visible) {
        target.layer.show();
      } else {
        target.layer.hide();
      }
    }

    const enabled = visible && this.autoRefresh;
    if (target.autoUpdate !== enabled) {
      if (target.dh.conf.validtime) {
        target.dh.setAutoUpdate('validtime', enabled);
      } else if (target.dh.conf.data) {
        target.dh.setAutoUpdate('data', enabled);
      } else {
        target.dh.setAutoUpdate('timelist', enabled);
      }
      this.dhAutoUpdate[type] = enabled;
    }

    if ([RADAR_TYPE_JP, RADAR_TYPE_KR, RADAR_TYPE_TW, RADAR_TYPE_XRAIN, RADAR_TYPE_NOWCAST, RADAR_TYPE_JMA_PRCINT].includes(type)) {
      this._updateTimeList();
    }

    this._coverageMerge();

    this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
  }

  isVisible(type) {
    return this.areaVisible[type];
  }

  getTimeInformation() {
    const timeList = this._visibleArea().filter(t => {
      return t !== RADAR_TYPE_ECHO_TOP_JP && t !== RADAR_TYPE_JMA_INFO && t !== RADAR_TYPE_PRCINT_ECHO_TOP;
    }).map(t => {
      return {
        id: t,
        area: t === RADAR_TYPE_JMA_PRCINT ? RADAR_TYPE_JMA_PRCINT_NAME : t ,
        time: this.areaValidtimeInfo[t]
      };
    });
    return {
      id: Radar.NAME,
      label: 'Radar',
      baseTime: timeList,
      baseTimeFormatter: time => {
        return `${translation(time.area)} ${formatTimeZoneDate(time.time)}`
      }
    }
  }

  getLegend() {
    return {
      id: Radar.NAME,
      label: 'Radar',
      grouping: 'radar',
      component: LegendRadar,
      contents: this
    }
  }

  getTimeController() {
    return this.radarTimeController;
  }

  applyElapsed(elapsed) {
    this.elapsed = elapsed;

    if (!this.ready) return;

    this._visibleArea().forEach(dataType => {
      const dh = this._getTarget(dataType).dh;
      if (dh.conf.validtime) {
        this._selectValidTime(dh.retained.validtime, dataType);
      } else {
        this._selectValidTime(dh.retained.timelist, dataType);
      }
    });
  }

  applyPlanETD(etd) {
    this.etd = etd;

    if (!this.ready) return;

    this._visibleArea().forEach(dataType => {
      const dh = this._getTarget(dataType).dh;
      if (dh.conf.validtime) {
        this._selectValidTime(dh.retained.validtime, dataType);
      } else {
        this._selectValidTime(dh.retained.timelist, dataType);
      }
    });
  }

  getCoverage() {
    return this.coverage;
  }
  setCoverage(coverage) {
    if (this.coverage === coverage) {
      return;
    }
    this.coverage = coverage;
    this.layerCoverageJP.setVisible(coverage && this.areaVisible[RADAR_TYPE_JP]);
    this.layerCoverageKR.setVisible(coverage && this.areaVisible[RADAR_TYPE_KR]);
    this.layerCoverageTW.setVisible(coverage && this.areaVisible[RADAR_TYPE_TW]);
    this.layerCoverageXRAIN.setVisible(coverage && this.areaVisible[RADAR_TYPE_XRAIN]);
    this.layerCoverageNOWCAST.setVisible(coverage && this.areaVisible[RADAR_TYPE_NOWCAST]);
    this.layerCoveragePrcint.setVisible(coverage && this.areaVisible[RADAR_TYPE_JMA_PRCINT]);
  }

  getEchoTop() {
    return this.echoTop;
  }

  getPrcintEchoTop() {
    return this.prcintEchoTop;
  }

  async setEchoTop(echoTop) {
    if (this.echoTop === echoTop) {
      return;
    }
    if (!this.areaInitialized[RADAR_TYPE_ECHO_TOP_JP]) {
      await this.initialize(RADAR_TYPE_ECHO_TOP_JP);
    }
    this.echoTop = echoTop;
    this.areaVisible[RADAR_TYPE_ECHO_TOP_JP] = this.echoTop;
    this.setVisible(this.echoTop, RADAR_TYPE_ECHO_TOP_JP);
  }

  async setPrcintEchoTop(echoTop) {
    if (this.prcintEchoTop === echoTop) {
      return;
    }
    if (!this.areaInitialized[RADAR_TYPE_PRCINT_ECHO_TOP]) {
      await this.initialize(RADAR_TYPE_PRCINT_ECHO_TOP);
    }
    this.prcintEchoTop = echoTop;
    this.areaVisible[RADAR_TYPE_PRCINT_ECHO_TOP] = this.prcintEchoTop;
    this.setVisible(this.prcintEchoTop, RADAR_TYPE_PRCINT_ECHO_TOP);
  }

  setAutoRefresh(autoFlg) {
    if (autoFlg === this.autoRefresh) {
      return;
    }

    this.autoRefresh = autoFlg;

    if (!this.ready) {
      return;
    }

    //表示中のコンテンツの自動更新を停止
    this._visibleArea().forEach(area => {
      if (area === RADAR_TYPE_JMA_INFO) {
        this._getTarget(area).dh.setAutoUpdate('data', this.autoRefresh);
      } else {
        this._getTarget(area).dh.setAutoUpdate('validtime', this.autoRefresh);
      }
      this.dhAutoUpdate[area] = this.autoRefresh;
    });
  }

  _visibleArea() {
    return Object.keys(this.areaVisible).sort().reduce((visibleAreaList, area) => {
      if (this.areaVisible[area]) {
        visibleAreaList.push(area)
      }
      return visibleAreaList;
    }, []);
  }

  /**
   * 自動更新モード時のvalid選択
   */
  _selectValidTime(ref, dataType) {
    let targetTime;
    if (!this.etd) {
      //プラン未選択時
      if (!this.autoRefresh) {
        //タイムコントローラ操作時にレーダーを追加した場合、タイムコントローラの選択時制
        targetTime = this.radarTimeController.getCurrentTime();
      } else {
        targetTime = Date.now();
      }
    } else {
      //プラン選択時
      targetTime = this.etd.getTime() + this.elapsed * 1000;
    }

    const vt = this._nearValidTime(ref, targetTime);
    if (vt) {
      const validTime = this.areaValidtimeInfo[dataType];
      if (validTime !== vt) {
        this.areaValidtimeInfo[dataType] = vt;
        this._updateDataset(dataType);
        this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
      }
    }
    if (dataType === this.timeCtrlTimeListArea
      || dataType === RADAR_TYPE_NOWCAST
      || (!this.timeCtrlTimeListArea && [RADAR_TYPE_JP, RADAR_TYPE_KR, RADAR_TYPE_TW, RADAR_TYPE_XRAIN, RADAR_TYPE_NOWCAST, RADAR_TYPE_JMA_PRCINT].includes(dataType))) {
      this._updateTimeList();
    }
  }

  /**
   * タイムコントロールモード時のvalid選択
   */
  _validateTime(dataType, data, displayTime, content) {
    const vt = this._nearValidTime(data.retained.validtime, displayTime);
    if (vt) {
      this.areaValidtimeInfo[dataType] = content.validtime = vt;
      return true;
    }
    return false
  }

  _nearValidTime(timeList, targetTime) {
    if (!timeList) return null;
    return nearValidTime(timeList.validtime, targetTime);
  }

  _updateDataset(dataType) {
    const target = this._getTarget(dataType);
    if (this.areaValidtimeInfo[dataType] !== '---') {

      if (dataType === RADAR_TYPE_ECHO_TOP_JP || dataType === RADAR_TYPE_JP) {
        //selectedTimeがJPレーダーと一致しない場合はエコートップは表示しない
        if (this.areaValidtimeInfo[RADAR_TYPE_JP] !== this.areaValidtimeInfo[RADAR_TYPE_ECHO_TOP_JP]) {
          this.layerEchoTopJP.clear();
          if (dataType === RADAR_TYPE_ECHO_TOP_JP) {
            return;
          }
        }
      } else if (dataType === RADAR_TYPE_PRCINT_ECHO_TOP || dataType === RADAR_TYPE_JMA_PRCINT) {
        if (this.areaValidtimeInfo[RADAR_TYPE_JMA_PRCINT] !== this.areaValidtimeInfo[RADAR_TYPE_PRCINT_ECHO_TOP]) {
          this.layerPrcintEchoTop.clear();
          if (dataType === RADAR_TYPE_PRCINT_ECHO_TOP) {
            return;
          }
        }
      }
      [].concat(target.layer).forEach(layer => {
        layer.set({
          validtime: this.areaValidtimeInfo[dataType]
        });
      });
    }
  }

  _getTarget(dataType) {
    let dh;
    let layer;
    let layerConfig;
    let timeCtrlLayer;
    switch (dataType) {
      case RADAR_TYPE_JP:
        dh = this.dhJP;
        layer = [this.layerJP, this.layerCoverageJP];
        layerConfig = [ConfigRadar.configRadarLayerJP, ConfigRadar.configRadarLayerCoverageJP];
        timeCtrlLayer =  [this.layerJP, this.layerCoverageJP];
        break;
      case RADAR_TYPE_KR:
        dh = this.dhKR;
        layer = [this.layerKR, this.layerCoverageKR];
        layerConfig = [ConfigRadar.configRadarLayerKR, ConfigRadar.configRadarLayerCoverageKR];
        timeCtrlLayer = [this.layerKR, this.layerCoverageKR];
        break;
      case RADAR_TYPE_TW:
        dh = this.dhTW;
        layer = [this.layerTW, this.layerCoverageTW];
        layerConfig = [ConfigRadar.configRadarLayerTW, ConfigRadar.configRadarLayerCoverageTW];
        timeCtrlLayer = [this.layerTW, this.layerCoverageTW];
        break;
      case RADAR_TYPE_XRAIN:
        dh = this.dhXRAIN;
        layer = [this.layerXRAIN, this.layerCoverageXRAIN];
        layerConfig = [ConfigRadar.configRadarLayerXRAIN, ConfigRadar.configRadarLayerCoverageXRAIN];
        timeCtrlLayer = [this.layerXRAIN, this.layerCoverageXRAIN];
        break;
      case RADAR_TYPE_NOWCAST:
        dh = this.dhNOWCAST;
        layer = [this.layerNOWCAST, this.layerCoverageNOWCAST];
        layerConfig = [ConfigRadar.configRadarLayerNowCast, ConfigRadar.configRadarLayerCoverageNowCast];
        timeCtrlLayer = [this.layerNOWCAST, this.layerCoverageNOWCAST];
        break;
      case RADAR_TYPE_ECHO_TOP_JP:
        dh = this.dhEchoTopJP;
        layer = this.layerEchoTopJP;
        layerConfig = ConfigRadar.configRadarLayerEchoTopJP;
        timeCtrlLayer = this.layerEchoTopJP;
        break;
      case RADAR_TYPE_JMA_INFO:
        dh = this.dhJmaInfoDh;
        break;
      case RADAR_TYPE_JMA_PRCINT:
        dh = this.dhPrcint;
        layer = [this.layerPrcint, this.layerCoveragePrcint];
        layerConfig = [ConfigRadar.configRadarLayerPrcint, ConfigRadar.configRadarLayerCoveragePrcint];
        timeCtrlLayer = [this.layerPrcint, this.layerCoveragePrcint];
        break;
      case RADAR_TYPE_PRCINT_ECHO_TOP:
        dh = this.dhPrcintEchoTop;
        layer = this.layerPrcintEchoTop;
        layerConfig = ConfigRadar.configRadarLayerPrcintEchoTop;
        timeCtrlLayer = this.layerPrcintEchoTop;
        break;
      default:
    }
    return {
      dh: dh,
      autoUpdate: this.dhAutoUpdate[dataType],
      layer: layer,
      layerConfig: layerConfig,
      timeCtrlLayer: timeCtrlLayer
    }
  }

  _coverageMerge() {
    if (!this.coverage) {
      return;
    }

    const areas = this._visibleArea();

    const mergeTarget = areas.reduce((accumulator, area) => {
      const coverageLayer = this._getCoverageLayer(area);
      if (coverageLayer) {
        accumulator.push(coverageLayer);
      }
      return accumulator;
    }, []);

    if (mergeTarget.length <= 1) {
      return;
    }

    if (this.currentMergeTargetLayer !== mergeTarget[0]) {
      //merge状態を解除
      if (this.currentMergeTargetLayer) {
        this.currentMergeTargetLayer.merge(null);
      }
      this.currentMergeTargetLayer = mergeTarget[0];
    }
    mergeTarget[0].merge(mergeTarget.slice(1));
  }

  _getCoverageLayer(type) {
    switch (type) {
      case RADAR_TYPE_JP:
        return this.layerCoverageJP;
      case RADAR_TYPE_KR:
        return this.layerCoverageKR;
      case RADAR_TYPE_TW:
        return this.layerCoverageTW;
      case RADAR_TYPE_XRAIN:
        return this.layerCoverageXRAIN;
      case RADAR_TYPE_NOWCAST:
        return this.layerCoverageNOWCAST;
      case RADAR_TYPE_JMA_PRCINT:
        return this.layerCoveragePrcint;
      default:
    }
  }

  _allLayers() {
    return [
      this.layerCoverageJP,
      this.layerCoverageKR,
      this.layerCoverageTW,
      this.layerCoverageXRAIN,
      this.layerCoverageNOWCAST,
      this.layerCoveragePrcint,
      this.layerJP,
      this.layerKR,
      this.layerTW,
      this.layerXRAIN,
      this.layerNOWCAST,
      this.layerPrcint,
      this.layerEchoTopJP,
      this.layerPrcintEchoTop
    ];
  }

  _updateTimeList() {
    this.timeCtrlTimeListArea = this._getTimeCtrlTimeListArea();
    const targetDh = this._getTarget(this.timeCtrlTimeListArea)?.dh;
    let validTimes = targetDh?.retained?.validtime?.validtime;
    if (!validTimes) {
      return;
    }

    //最新から1時間以内のものにfilter
    validTimes = Array.from(validTimes).sort();
    const latest = this.timeCtrlTimeListArea !== RADAR_TYPE_NOWCAST ? validTimes[validTimes.length - 1] : this._nearValidTime(targetDh.retained.validtime, Date.now());
    const latestTime = new WRAP.Core.DateTime(latest)
    this.timeList = validTimes.filter(vt => {
      const diff = (new WRAP.Core.DateTime(vt)).diff(latestTime)
      return 0 <= diff && diff <= 3600;
    });
    // 降水ナウキャストが表示されている場合、latestから未来方向に1時間追加
    if (this.areaVisible[RADAR_TYPE_NOWCAST]) {
      let forecastTimes = this.dhNOWCAST?.retained?.validtime?.validtime;
      if (forecastTimes) {
        Array.from(forecastTimes).sort().forEach(ft => {
          const diff = latestTime.diff(new WRAP.Core.DateTime(ft));
          if (0 < diff && diff <= 3600) {
            this.timeList.push(ft);
          }
        });
      }
    }
    if (this.autoRefresh) {
      this.radarTimeController.setCurrentTime(latest);
    }
    this.radarTimeController.adjustCurrentTime();
    this.radarTimeController.invalidate();
  }

  _updateMissing(dataType, missing) {
    const prev = this.areaMissing[dataType];
    if (prev !== missing) {
      this.areaMissing[dataType] = missing;
    }
  }

  _getTimeCtrlTimeListArea() {
    const visibleArea = [
      RADAR_TYPE_JP,
      RADAR_TYPE_JMA_PRCINT,
      RADAR_TYPE_KR,
      RADAR_TYPE_TW,
      RADAR_TYPE_XRAIN,
      RADAR_TYPE_NOWCAST
    ].filter(area => this.isVisible(area));
    //表示状態＆欠測していない。
    const noMissingArea = visibleArea.filter(area => !this.areaMissing[area]);
    //すべて欠測であれば、表示状態onのもの
    return noMissingArea[0] || visibleArea[0];
  }

  getTransmittance() {
    return this.transmittance;
  }

  setTransmittance(transmittance) {
    if (this.transmittance === transmittance) {
      return;
    }
    this.transmittance = transmittance;
    const opacity = Math.round(255 / 100 * (100 - this.transmittance));
    ConfigRadar.expoRadarLayerConfig.Attributes.style.default.palette.slice(2, 255).forEach(palette => {
      palette[3] = opacity;
    });

    [this.layerJP, this.layerPrcint, this.layerKR, this.layerTW, this.layerNOWCAST, this.layerXRAIN].forEach(layer => {
      layer.invalidate();
    });
  }
}

export default Radar;
